import { Preferences } from "@capacitor/preferences";
import { requestCloudApi } from "@/integrations/cloud/cloudApiClient";
import { isCloudConfigured } from "@/integrations/cloud/cloudConfig";

const ACCESS_TOKEN_KEY = "ipm_cloud_access_token";

export interface CloudIdentity {
  id: string;
  email: string | null;
}

export interface AuthSecurityState {
  aal: "aal1" | "aal2" | "unknown";
}

type SignInResponse = {
  accessToken: string;
  expiresAt: string;
  user: {
    id: string;
    email: string;
    emailVerifiedAt: string;
  };
};

const requireCloudConfigured = () => {
  if (!isCloudConfigured) {
    throw new Error(
      "Cloud backend is not configured. Set VITE_CLOUD_API_URL."
    );
  }
};

const setAccessToken = async (value: string) => {
  await Preferences.set({ key: ACCESS_TOKEN_KEY, value });
};

const clearAccessToken = async () => {
  await Preferences.remove({ key: ACCESS_TOKEN_KEY });
};

const withAuthHeaders = async (
  headers: Record<string, string> = {}
): Promise<Record<string, string>> => {
  const token = await getAccessToken();
  if (!token) {
    throw new Error("Sign in to continue.");
  }

  return {
    ...headers,
    authorization: `Bearer ${token}`
  };
};

export const getCurrentIdentity = async (): Promise<CloudIdentity | null> => {
  requireCloudConfigured();
  const token = await getAccessToken();
  if (!token) return null;

  try {
    const payload = await requestCloudApi<{
      user: { id: string; email: string };
    }>("/auth/me", {
      method: "GET",
      headers: {
        authorization: `Bearer ${token}`
      }
    });

    return {
      id: payload.user.id,
      email: payload.user.email
    };
  } catch (error) {
    await clearAccessToken();
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (message.includes("expired") || message.includes("sign in")) {
        return null;
      }
    }
    throw error;
  }
};

export const getAuthSecurityState = async (): Promise<AuthSecurityState> => {
  // Custom Cloudflare auth currently does not expose MFA assurance levels.
  return { aal: "aal1" };
};

const validatePassword = (password: string): void => {
  if (password.length < 12) {
    throw new Error("Password must be at least 12 characters.");
  }
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSymbol = /[^A-Za-z0-9]/.test(password);

  if (!hasUpper || !hasLower || !hasNumber || !hasSymbol) {
    throw new Error(
      "Password must include uppercase, lowercase, number, and symbol."
    );
  }
};

export const signUpWithEmail = async (
  email: string,
  password: string
): Promise<void> => {
  requireCloudConfigured();
  validatePassword(password);

  await requestCloudApi("/auth/sign-up", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({
      email,
      password
    })
  });
};

export const resendSignUpEmail = async (email: string): Promise<void> => {
  requireCloudConfigured();
  await requestCloudApi("/auth/resend-verification", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({ email })
  });
};

export const signInWithEmail = async (
  email: string,
  password: string
): Promise<CloudIdentity> => {
  requireCloudConfigured();

  const payload = await requestCloudApi<SignInResponse>("/auth/sign-in", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({
      email,
      password
    })
  });

  await setAccessToken(payload.accessToken);
  return {
    id: payload.user.id,
    email: payload.user.email
  };
};

export const signOutCloud = async (): Promise<void> => {
  await clearAccessToken();
};

export const getAccessToken = async (): Promise<string | null> => {
  const current = await Preferences.get({ key: ACCESS_TOKEN_KEY });
  const token = current.value?.trim();
  return token && token.length > 0 ? token : null;
};

export const authJsonHeaders = async (): Promise<Record<string, string>> =>
  withAuthHeaders({
    "content-type": "application/json"
  });
